import { Injectable } from "@angular/core"
import { environment } from "../../../../environments/environment"
import { SchoolGetRequest } from "../models/school/school-get-request"
import { HttpClient, HttpHeaders } from "@angular/common/http"
import { toHttpParams } from "src/app/core/helpers/http-params-extensions"
import { SchoolPostPutRequest } from "../models/school/school-post-request"
import { SchoolInfo } from "../models/school/school-info"
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  map,
  of,
  tap,
} from "rxjs"
import { HEADERS } from "./const/http-header-interception"

@Injectable()
export class SchoolService {
  private post_school_url: string
  private get_school_url: string
  private get_school_schoolOptions_url: string
  private get_school_types_url: string
  private get_school_count_url: string
  private get_school_schoolOptions_tenantId_url: string
  private get_tenant_options_url: string
  private get_school_id_url: string
  private put_school_url: string
  private get_class_school_id_url: string
  private get_subjects: string

  lexImportSuccess = new Subject()

  private schoolOptionsSubject = new BehaviorSubject<SchoolOption[]>([])
  private schoolOptions$ = this.schoolOptionsSubject.asObservable()
  private isLoaded = false

  constructor(private httpClient: HttpClient) {
    const routes = {}

    const updatedSchoolUrl = environment.endpointUpdatedSchool.url

    // tslint:disable-next-line: forin
    for (const updatedSchoolRoute in environment.endpointUpdatedSchool.routes) {
      routes[updatedSchoolRoute] =
        updatedSchoolUrl +
        environment.endpointUpdatedSchool.routes[updatedSchoolRoute]
    }

    const schoolUrl = environment.endpointSchool.url

    for (const schoolRoute in environment.endpointSchool.routes) {
      if (!routes[schoolRoute]) {
        routes[schoolRoute] =
          schoolUrl + environment.endpointSchool.routes[schoolRoute]
      }
    }

    this.post_school_url = routes["post_school"]
    this.put_school_url = routes["put_school"]
    this.get_school_url = routes["get_school"]
    this.get_school_id_url = routes["get_school_id"]
    this.get_school_schoolOptions_url = routes["get_school_schoolOptions"]
    this.get_school_types_url = routes["get_school_types"]
    this.get_school_count_url = routes["get_school_count"]

    this.get_school_schoolOptions_tenantId_url =
      routes["get_school_schoolOptions_tenantId"]
    this.get_tenant_options_url = routes["get_tenant_options"]

    this.get_class_school_id_url = routes["get_school_class"]
    this.get_subjects = routes["get_subjects"]
  }

  postSchool(school: SchoolPostPutRequest) {
    return this.httpClient.post(this.post_school_url, school, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  putSchool(school: SchoolPostPutRequest) {
    if (!school.id) {
      throw new Error("You must inform a school identification.")
    }
    return this.httpClient.put(
      this.put_school_url.replace("{schoolId}", school.id),
      school,
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getAllSchools(school: SchoolGetRequest) {
    const params = toHttpParams(school)
    return this.httpClient.get<any>(this.get_school_url, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }
  getSchoolTypes() {
    return this.httpClient.get(this.get_school_types_url, {
      headers: new HttpHeaders(HEADERS),
    })
  }
  getTenantOptions() {
    return this.httpClient.get(this.get_tenant_options_url, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  getSchoolOptions(): Observable<SchoolOption[]> {
    if (this.isLoaded) {
      return this.schoolOptions$
    } else {
      return this.httpClient
        .get<SchoolOption[]>(this.get_school_schoolOptions_url)
        .pipe(
          tap((options) => {
            this.schoolOptionsSubject.next(options)
            this.isLoaded = true
          }),
          map((options) => {
            options.forEach((option) => (option.externalIntegrationId = null))
            return options
          }),
          catchError((error) => {
            return of([])
          })
        )
    }
  }

  GetSchoolCountByTenantId() {
    return this.httpClient.get(this.get_school_count_url, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  getSchoolOptionsByTenantID(value: string) {
    return this.httpClient.get(
      this.get_school_schoolOptions_tenantId_url.replace("{tenantId}", value),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getOne(schoolId: string): Observable<SchoolInfo> {
    return this.httpClient.get<SchoolInfo>(
      this.get_school_id_url.replace("{schoolId}", schoolId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getSchoolGroups(schoolId: string) {
    return this.httpClient.get<any>(
      this.get_class_school_id_url.replace("{schoolId}", schoolId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getSubjectsBySchoolIdAndGradeId(schoolId: string, gradeId: string) {
    return this.httpClient.get<any[]>(
      this.get_subjects
        .replace("{schoolId}", schoolId)
        .replace("{gradeId}", gradeId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }
}
